import type { TagTone } from '@dayon/design-system';
import { formatPesos, formatRate } from '@dayon/money';

/**
 * §5 Affiliate management — the shapes the screen reads, and the vocabulary it
 * renders them in.
 *
 * Split out of `Affiliates.tsx` when the module grew its write half. Everything
 * here is shared by at least two of the screen's pieces: the table, the detail
 * drawer, the rate card and the four dialogs. Nothing here holds state.
 *
 * Snake_case field names are the SQL SELECT list of
 * `services/api/src/modules/affiliate/admin.ts`, not a style slip. The portal's
 * own routes are camelCase because they are shaped for a screen; this endpoint
 * returns rows.
 */

/** One row of GET /api/admin/affiliates. */
export interface AffiliateRow {
  id: string;
  /** AF-1201 */
  ref: string;
  name: string;
  source_kind: string;
  /** PENDING · ACTIVE · REVIEW · SUSPENDED */
  status: string;
  /** Set by the `affiliate.quality-review` worker job when conversion falls. */
  quality_review_opened_at: string | null;
  suspended_reason: string | null;
  /** identity.admin_user ids. Null on every row the seed wrote. */
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  brand: string;
  /** platform.brand.domain — what makes a copyable link possible at all. */
  brand_domain: string;
  /**
   * REVSHARE · CPA · HYBRID, off the LIVE plan (`valid_to IS NULL`). Null is a
   * real state: the plan join is a LEFT JOIN, and an affiliate with no live
   * plan earns nothing no matter what its status says.
   */
  model: string | null;
  revshare_bp: string | null;
  cpa_amount: string | null;
  cpa_hold_days: string | null;
  cpa_min_deposit: string | null;

  /** Lifetime. */
  signups: string;
  ftd: string;
  clicks: string;
  /** The same three over the 30 gaming days the design labels its columns with. */
  signups_30d: string;
  ftd_30d: string;
  clicks_30d: string;
  links: string;

  /** Booked in a period that has NOT been settled. This is what "unpaid" means. */
  accrued_commission: string;
  /** Every period result, settled or not. */
  booked_commission: string;
  /** What actually left, through affiliate.affiliate_payout. */
  paid_commission: string;
  clawed_back: string;
  last_paid_at: string | null;

  /**
   * Whether this affiliate can SIGN IN — NONE · INVITED · ACTIVE · SUSPENDED.
   *
   * 'NONE' is a real state, not a missing value. Nothing in the API used to
   * write `identity.partner_user`, so every affiliate the console had ever
   * filed had no login at all and no screen said so; the operator found out
   * when the partner rang. It is a different fact from an outstanding invite,
   * and only one of the two is fixed by re-issuing a link.
   */
  portal_status: string;
  /** The address that signs in, or null when there is no login. */
  portal_email: string | null;

  /** ftd ÷ signups in basis points; null when the affiliate has no signups. */
  conversion_bp: string | null;
  /** The same, over the window — the half of the auto-review rule that runs. */
  conversion_bp_30d: string | null;
  /** Centavos per click: accrued commission ÷ 30-day clicks. Null with no clicks. */
  epc: string | null;

  /** Player quality. Each share carries its own denominator. */
  avg_first_deposit: string | null;
  ftd_players: string;
  retained_30d: string;
  bonus_only: string;
  referred_players: string;
}

/** One row of GET /api/admin/affiliates/plans — the rate card, grouped by shape. */
export interface PlanRow {
  model: string;
  revshare_bp: string | null;
  cpa_amount: string | null;
  cpa_hold_days: string;
  /** The CPA qualifying floor. */
  cpa_min_deposit: string | null;
  affiliates: string;
}

/**
 * The window every "30d" figure on the screen was measured over.
 *
 * NOT called `Window`: that is a DOM global, and a local type of the same name
 * shadows it wherever this module is imported.
 */
export interface MeasuredWindow {
  days: number;
  from: string;
  to: string;
}

export interface AffiliateListPayload {
  items: AffiliateRow[];
  window: MeasuredWindow;
}

/** One tracking link, with the assembled URL the server built. */
export interface LinkRow {
  id: string;
  sub_id: string;
  channel: string;
  active: boolean;
  created_at: string;
  campaign: string | null;
  clicks: string;
  clicks_30d: string;
  signups: string;
  ftd: string;
  /** `peryaplay.ph/a/af1201?s=sub_home` — assembled server-side. */
  url: string;
}

export interface PayoutRow {
  ref: string;
  amount: string;
  status: string;
  is_early: boolean;
  handling_fee: string;
  paid_at: string | null;
  created_at: string;
  period_ref: string | null;
  initiated_by: string | null;
  approved_by: string | null;
}

export interface ClawbackRow {
  amount: string;
  reason_code: string;
  note: string | null;
  created_at: string;
  period_ref: string | null;
  initiated_by: string | null;
  approved_by: string | null;
}

export interface PeriodRow {
  ref: string;
  period_start: string;
  period_end: string;
  status: string;
  paid_at: string | null;
  net_revenue: string;
  revshare_commission: string;
  cpa_commission: string;
  net_commission: string;
  carry_in: string;
  carry_out: string;
  held: boolean;
  clicks: string;
  signups: string;
  first_deposits: string;
}

export interface AffiliateDetail {
  links: LinkRow[];
  payouts: PayoutRow[];
  clawbacks: ClawbackRow[];
  periods: PeriodRow[];
  window: MeasuredWindow;
}

/** GET /api/admin/affiliates/thresholds — a closed allow-list, keyed by setting. */
export interface Thresholds {
  items: Record<string, { value: string; description: string | null }>;
  missing: string[];
}

export const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  REVIEW: 'Review',
  SUSPENDED: 'Suspended',
};

/**
 * Four states, three treatments, exactly as the design separates them.
 * Active is the settled state (filled). Pending and Review both want a human
 * and are outlined. Suspended is quiet: it is a decision already taken, not an
 * item of work — and per the module rule, suspending stops new attribution but
 * existing players keep earning revshare, so it is not an "off" switch either.
 */
export const STATUS_TONE: Record<string, TagTone> = {
  ACTIVE: 'accent',
  PENDING: 'outline',
  REVIEW: 'outline',
  SUSPENDED: 'quiet',
};

/**
 * Portal access, in the four words an operator needs.
 *
 * "No login" is deliberately not "Inactive" or a dash: it is the state the
 * console can DO something about, and naming it as an absence rather than as a
 * status is what makes the control beside it read as the fix.
 */
export const PORTAL_STATUS_LABEL: Record<string, string> = {
  NONE: 'No login',
  INVITED: 'Invited',
  ACTIVE: 'Can sign in',
  SUSPENDED: 'Suspended',
};

/**
 * Only ACTIVE is filled. An invite is outstanding work and is outlined, like
 * every other "wants a human" state on this screen; no login at all is quiet,
 * because it is the resting state of an affiliate nobody has provisioned yet
 * and a red badge on every row would train the eye to ignore it.
 */
export const PORTAL_STATUS_TONE: Record<string, TagTone> = {
  NONE: 'quiet',
  INVITED: 'outline',
  ACTIVE: 'accent',
  SUSPENDED: 'quiet',
};

/** GET /api/admin/affiliates/:id/portal-access. */
export interface PortalAccess {
  partnerKind: 'AFFILIATE';
  partnerId: string;
  partnerRef: string;
  partnerName: string;
  partnerStatus: string;
  canProvision: boolean;
  /**
   * The roles this press could actually create, from the SERVER.
   *
   * `['OWNER']` while the affiliate has no owner login; the other three once it
   * has one, because a partner has exactly one owner and
   * `partner_user_one_owner_idx` says so in the database. The dropdown is built
   * from this rather than from a list in the console, so the day the model
   * changes the screen follows without a second deploy.
   */
  provisionableRoles: string[];
  /** Why not, in a sentence — null when it would be accepted. */
  refusal: string | null;
  /**
   * EVERY login under this partner, owner first — not only the owner.
   *
   * `access` below is still the OWNER's, because "this partner's login" has
   * always meant theirs on this panel. This is the office beside them, and it
   * is a separate field because they are separate questions.
   */
  staff: {
    id: string;
    email: string;
    displayName: string;
    portalRole: string;
    status: string;
    lastSignInAt: string | null;
    /** Whether a password was ever set. INVITED with no password cannot sign in. */
    hasPassword: boolean;
    /** An outstanding ACCEPTANCE link, or null. */
    inviteExpiresAt: string | null;
    /**
     * An outstanding RESET link, or null — the one state the old panel could
     * not describe. A row with this set is ACTIVE and cannot sign in: the
     * account is fine, the credential is pending.
     */
    resetExpiresAt: string | null;
  }[];
  access: {
    id: string;
    email: string;
    displayName: string;
    portalRole: string;
    status: string;
    lastSignInAt: string | null;
    /** The OUTSTANDING link, or null. `expired` is computed by the server. */
    invite: { issuedAt: string; expiresAt: string; expired: boolean } | null;
  } | null;
}

/** What provisioning and re-issuing both return. The link is shown ONCE. */
export interface PortalInviteLink {
  partnerUserId: string;
  email: string;
  displayName: string;
  status: string;
  /** `/accept-invite#token=…`, relative to the affiliate portal. */
  linkPath: string;
  /** The same, against AFFILIATE_PORTAL_ORIGIN — assembled by the server. */
  linkUrl: string;
  expiresAt: string;
  supersededLinks: number;
  /**
   * THE OUTBOX'S OWN VERDICT for the message this request recorded, not a
   * sentence the server phrased.
   *
   *   PENDING    — a worker hands it to the provider within the minute.
   *   SUPPRESSED — recorded and NOT delivered: this environment's
   *                MAIL_OUTBOUND_MODE lets nobody receive mail, or the address
   *                stands on the suppression list.
   */
  delivery: { messageRef: string; status: string; to: string };
}

/**
 * POST /api/admin/affiliates/:id/portal-access/reset-password.
 *
 * The same shape as an invite link plus the two claims that make a reset a
 * different act from re-issuing one, both asserted by the SERVER rather than by
 * this console: the partner's live sessions are already over, and the password
 * they were using has already stopped working.
 */
export interface PortalResetLink extends PortalInviteLink {
  portalRole: string;
  sessionsEnded: true;
  oldPasswordStopsNow: true;
}

export const MODEL_TYPE_LABEL: Record<string, string> = {
  REVSHARE: 'Revenue share',
  CPA: 'Cost per acquisition',
  HYBRID: 'Hybrid',
};

/**
 * The FTD-conversion floor, as a LAST RESORT.
 *
 * The real value is `platform.setting['affiliate.quality_review_ftd_pct']`, and
 * the screen reads it from `GET /api/admin/affiliates/thresholds` — a route
 * that returns two named keys to `partners.view` rather than the whole settings
 * table to `access.manage`, which is why the number on screen finally follows
 * the row. This constant is only reached when that read fails, and every place
 * that uses it says so instead of printing a bare percentage.
 */
export const FALLBACK_FTD_FLOOR_BP = 500;

/**
 * The chargeback half of the auto-review rule, and the number the design
 * states. It has no `platform.setting` row, no `risk.risk_rule` behind it and
 * no chargeback table to measure against — so it is only ever printed inside a
 * sentence explaining that it cannot be evaluated.
 */
export const CHARGEBACK_CEILING_BP = 200;

export type StatusFilter = 'ALL' | 'ACTIVE' | 'PENDING' | 'REVIEW' | 'SUSPENDED';

/** "Status chips are mutually exclusive", so this is a radio group, not a toolbar. */
export const STATUS_FILTERS: readonly { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'REVIEW', label: 'Review' },
  { key: 'SUSPENDED', label: 'Suspended' },
];

/**
 * The design sorts by clicking a header. `DataTable` renders plain `<th>` text
 * and exposes no header handler, and rebuilding the table to add one would fork
 * the component every other module shares — so the same sorts live in a
 * labelled control and the active column still carries the arrow.
 */
export type SortKey = 'DEFAULT' | 'NAME' | 'SIGNUPS' | 'FTD' | 'COMMISSION';

export const SORTS: readonly { key: SortKey; label: string }[] = [
  { key: 'DEFAULT', label: 'Needs attention first' },
  { key: 'NAME', label: 'Affiliate name' },
  { key: 'SIGNUPS', label: 'Signups (30 days)' },
  { key: 'FTD', label: 'FTDs (30 days)' },
  { key: 'COMMISSION', label: 'Accrued commission' },
];

/** "Revshare 30.0%" / "CPA ₱850.00" / "Hybrid ₱400.00 + 15.0%". */
export function describeModel(
  model: string | null,
  revshareBp: string | null,
  cpaAmount: string | null,
): string {
  if (model === 'REVSHARE' && revshareBp !== null) return `Revshare ${formatRate(Number(revshareBp))}`;
  if (model === 'CPA' && cpaAmount !== null) return `CPA ${formatPesos(BigInt(cpaAmount))}`;
  if (model === 'HYBRID' && cpaAmount !== null && revshareBp !== null) {
    return `Hybrid ${formatPesos(BigInt(cpaAmount))} + ${formatRate(Number(revshareBp))}`;
  }
  return model ?? 'No live plan';
}

/** Appends the design's ' ↑' / ' ↓' to whichever header is carrying the sort. */
export function sortable(
  header: string,
  key: SortKey,
  sort: SortKey,
  descending: boolean,
): string {
  return sort === key ? `${header}${descending ? ' ↓' : ' ↑'}` : header;
}

/**
 * A share, in basis points, of a denominator that may be zero.
 *
 * Null rather than 0 when there is nothing to divide by. "0% retention" on a
 * partner with no depositing players is a claim about traffic quality; "no
 * depositing players yet" is a different fact, and only one of them should make
 * an operator reach for the suspend button.
 */
export function shareBp(numerator: string, denominator: string): number | null {
  const d = Number(denominator);
  if (d === 0) return null;
  return Math.round((Number(numerator) / d) * 10000);
}

/** "31 Aug 2026", in the platform's own timezone rather than the browser's. */
const dayFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Manila',
});

export const formatDay = (iso: string | null): string =>
  iso === null ? '—' : dayFmt.format(new Date(iso));
